import { useState, useEffect, useRef, useCallback, Fragment } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  Upload, Download, FileSpreadsheet, CheckCircle2, AlertTriangle,
  X, ArrowRight, Users, Building2, ShoppingBag, Kanban, History,
  ChevronRight, RotateCcw, ArrowLeft, Info, DollarSign, TrendingUp
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useAppearance } from "../../contexts/AppearanceContext";
import { toast } from "../../components/ui/sonner";
import { formatDateTime } from "../../lib/dateUtils";
import { Link2, Undo2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODULE_ICONS = {
  contatos: Users, empresas: Building2, produtos: ShoppingBag, negocios: Kanban,
};

const MODULE_LABELS = {
  contatos: "Contatos", empresas: "Empresas", produtos: "Produtos",
  negocios: "Negócios", unified: "Unificado",
};

const STEP_KEYS = ["step1", "step2", "step3", "stepConfig", "step4", "step5"];

// ── Timeline step bar ──────────────────────────────────────────────────────────
function StepBar({ step, t, tc500 }) {
  return (
    <div className="flex items-start mt-4 max-w-2xl mx-auto w-full mb-8">
      {STEP_KEYS.map((sk, i) => {
        const done = i < step, active = i === step;
        return (
          <Fragment key={sk}>
            {/* Node: circle + label */}
            <div className="flex flex-col items-center gap-2 flex-shrink-0">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all flex-shrink-0
                ${active ? "text-white border-transparent shadow-md" : done ? "text-white border-transparent" : "text-slate-400 border-slate-200 bg-white"}`}
                style={active ? { backgroundColor: tc500, boxShadow: `0 0 0 4px ${tc500}20` } : done ? { backgroundColor: tc500 } : undefined}>
                {done ? <CheckCircle2 className="w-4 h-4" /> : <span>{i + 1}</span>}
              </div>
              <span className={`text-[10px] font-medium whitespace-nowrap text-center
                ${active ? "font-semibold text-slate-800" : done ? "text-slate-500" : "text-slate-400"}`}>
                {t(`import.${sk}`)}
              </span>
            </div>
            {/* Connector line — mt-[18px] = half of h-9(36px), centering on circle */}
            {i < STEP_KEYS.length - 1 && (
              <div className="flex-1 h-0.5 mt-[18px] mx-1 transition-colors"
                style={{ backgroundColor: i < step ? tc500 : "#e2e8f0" }} />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

// ── Tooltip ────────────────────────────────────────────────────────────────────
function Tooltip({ text, children }) {
  return (
    <span className="relative group inline-flex items-center">
      {children}
      <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-slate-900 text-white text-[11px] leading-relaxed rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity z-50 text-center shadow-xl">
        {text}
        <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900" />
      </span>
    </span>
  );
}

export default function ImportPage() {
  const { t } = useTranslation();
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3B82F6";

  const [step, setStep] = useState(0);
  const [module, setModule] = useState("");
  const [templateInfo, setTemplateInfo] = useState(null);
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [dedup, setDedup] = useState("skip");
  const [textCase, setTextCase] = useState("none"); // "none" | "upper" | "capitalize"
  const [savedMapping, setSavedMapping] = useState(null);
  const [tipoNegocio, setTipoNegocio] = useState(""); // recorrente_setup | pontual | recorrente_produto
  const [valorMapping, setValorMapping] = useState({}); // { mrr: "col", setup: "col" } or { valor_negocio: "col" }
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [undoing, setUndoing] = useState(null);
  const [undoConfirmItem, setUndoConfirmItem] = useState(null); // item to confirm undo
  // Pipeline config state
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState("");
  const [pipelineStages, setPipelineStages] = useState([]);
  const [selectedStage, setSelectedStage] = useState("");
  // Stage value mapping: XLS stage value → pipeline stage id
  const [stageValueMapping, setStageValueMapping] = useState({});
  const [xlsStageValues, setXlsStageValues] = useState([]);
  const [xlsStageColName, setXlsStageColName] = useState(""); // nome da coluna do arquivo
  // Status value mapping: XLS status value → { status: 'ganho'|'perdido'|null, dateCol: string }
  const [statusValueMapping, setStatusValueMapping] = useState({});
  const [xlsStatusValues, setXlsStatusValues] = useState([]);
  // Responsavel value mapping: XLS responsavel value → user_id
  const [responsavelValueMapping, setResponsavelValueMapping] = useState({});
  const [xlsResponsavelValues, setXlsResponsavelValues] = useState([]);
  const [systemUsers, setSystemUsers] = useState([]);
  const dropRef = useRef(null);

  // ── localStorage helpers ─────────────────────────────────────────────────
  const MAPPING_KEY = (mod) => `import_mapping_${mod}`;
  const loadMapping = (mod) => {
    try { return JSON.parse(localStorage.getItem(MAPPING_KEY(mod)) || "null"); }
    catch { return null; }
  };
  const saveMapping = (mod, map) => {
    try { localStorage.setItem(MAPPING_KEY(mod), JSON.stringify(map)); }
    catch { /* quota exceeded or private browsing */ }
  };

  // Auto-save mapping to localStorage whenever it changes
  useEffect(() => {
    if (module && Object.keys(mapping).length > 0) {
      saveMapping(module, mapping);
    }
  }, [mapping, module]);

  // Extract responsavel values when entering step 3
  useEffect(() => {
    if (step !== 3 || (module !== "negocios" && module !== "unified")) return;
    // Always load system users
    axios.get(`${API}/users/responsaveis`).then(res => {
      setSystemUsers(res.data || []);
    }).catch(() => {});
    // Extract from file if column is mapped
    const respCol = Object.keys(mapping).find(col => mapping[col] === "responsavel");
    if (respCol && rows.length > 0) {
      const unique = [...new Set(rows.map(r => String(r[respCol] || "").trim()).filter(Boolean))];
      setXlsResponsavelValues(unique);
    } else {
      setXlsResponsavelValues([]);
    }
  }, [step, module, mapping]); // eslint-disable-line


  // ── Helpers ──────────────────────────────────────────────────────────────
  const applySavedMapping = (mod) => {
    const saved = loadMapping(mod);
    setSavedMapping(saved);
    return saved;
  };

  const fetchTemplate = async (mod) => {
    const res = await axios.get(`${API}/import/template/${mod}`, { withCredentials: true });
    setTemplateInfo(res.data);
  };

  const fetchPipelines = async () => {
    try {
      const res = await axios.get(`${API}/pipelines`, { withCredentials: true });
      setPipelines(res.data || []);
      if (res.data?.length > 0 && !selectedPipeline) {
        const first = res.data[0];
        setSelectedPipeline(first.id);
        fetchStages(first.id);
      }
      // Extract unique status values from XLS
      const statusCol = Object.keys(mapping).find(col => mapping[col] === "status");
      if (statusCol && rows.length > 0) {
        const unique = [...new Set(rows.map(r => String(r[statusCol] || "").trim()).filter(Boolean))];
        setXlsStatusValues(unique);
        // Auto-map obvious patterns
        const autoStatus = {};
        unique.forEach(val => {
          const v = val.toLowerCase();
          if (["ganho","won","ganhado","fechado","closed","sim","yes","1","concluido","concluído"].some(k => v.includes(k)))
            autoStatus[val] = { status: "ganho", dateCol: "" };
          else if (["perdido","lost","cancelado","nao","não","no","0","recusado"].some(k => v.includes(k)))
            autoStatus[val] = { status: "perdido", dateCol: "" };
          else
            autoStatus[val] = { status: null, dateCol: "" };
        });
        setStatusValueMapping(autoStatus);
      } else {
        setXlsStatusValues([]);
        setStatusValueMapping({});
      }
      // Extract unique responsavel values from XLS
      const respCol = Object.keys(mapping).find(col => mapping[col] === "responsavel");
      let respValues = [];
      if (respCol && rows.length > 0) {
        respValues = [...new Set(rows.map(r => String(r[respCol] || "").trim()).filter(Boolean))];
      }
      setXlsResponsavelValues(respValues);
      // Load system users for responsavel mapping
      try {
        const usersRes = await axios.get(`${API}/users/responsaveis`);
        setSystemUsers(usersRes.data || []);
        if (respValues.length > 0) {
          const autoMap = {};
          respValues.forEach(val => {
            const match = (usersRes.data || []).find(u =>
              u.name.toLowerCase() === val.toLowerCase() ||
              u.name.toLowerCase().includes(val.toLowerCase()) ||
              val.toLowerCase().includes(u.name.toLowerCase())
            );
            if (match) autoMap[val] = match.user_id;
          });
          setResponsavelValueMapping(autoMap);
        }
      } catch { /* silent */ }
    } catch { /* silent */ }
  };

  const fetchStages = async (pipelineId) => {
    try {
      const res = await axios.get(`${API}/pipeline-stages?pipeline_id=${pipelineId}`, { withCredentials: true });
      const stages = (res.data || []).sort((a, b) => a.order - b.order);
      setPipelineStages(stages);
      if (stages.length > 0) setSelectedStage(stages[0].id);
      // Extract unique stage values from XLS (reverse-look: which column maps to "etapa")
      const etapaCol = Object.keys(mapping).find(col => mapping[col] === "etapa");
      if (etapaCol && rows.length > 0) {
        const unique = [...new Set(rows.map(r => String(r[etapaCol] || "").trim()).filter(Boolean))];
        setXlsStageValues(unique);
        setXlsStageColName(etapaCol);
        // Auto-map by name similarity (case-insensitive exact match first)
        const autoMap = {};
        unique.forEach(val => {
          const match = stages.find(s =>
            s.name.toLowerCase() === val.toLowerCase() ||
            s.name.toLowerCase().includes(val.toLowerCase()) ||
            val.toLowerCase().includes(s.name.toLowerCase())
          );
          if (match) autoMap[val] = match.id;
        });
        setStageValueMapping(autoMap);
      } else {
        setXlsStageValues([]);
        setXlsStageColName("");
        setStageValueMapping({});
      }
    } catch { /* silent */ }
  };

  const fetchHistory = async () => {
    const res = await axios.get(`${API}/import/history`, { withCredentials: true });
    setHistory(res.data);
    setShowHistory(true);
  };

  const undoImport = async (h) => {
    setUndoConfirmItem(h);
  };

  const confirmUndo = async () => {
    const h = undoConfirmItem;
    if (!h) return;
    setUndoConfirmItem(null);
    setUndoing(h.id);
    try {
      await axios.delete(`${API}/import/history/${h.id}/undo`, { withCredentials: true });
      toast.success(t("import.undoDone"));
      fetchHistory();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao desfazer."); }
    setUndoing(null);
  };

  const parseFile = useCallback((f) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { defval: "" });
        if (!raw.length) { toast.error("Arquivo vazio ou sem dados."); return; }
        const newHeaders = Object.keys(raw[0]);
        setRows(raw);
        setHeaders(newHeaders);
        setFile(f);
        // Auto-apply saved mapping for matching columns
        const saved = loadMapping(module);
        if (saved) {
          const autoMap = {};
          newHeaders.forEach(h => { if (saved[h]) autoMap[h] = saved[h]; });
          const count = Object.keys(autoMap).length;
          setMapping(autoMap);
          if (count > 0) toast.success(`Mapeamento anterior restaurado automaticamente (${count} colunas).`);
        } else {
          setMapping({});
        }
        setStep(2);
      } catch { toast.error("Erro ao ler o arquivo. Verifique se é .xlsx ou .csv válido."); }
    };
    reader.readAsArrayBuffer(f);
  }, [module]); // module como dependência para evitar stale closure

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) parseFile(f);
  };

  const downloadTemplate = () => {
    if (!templateInfo) return;
    const wb = XLSX.utils.book_new();
    const header = templateInfo.fields.map(f => f.label.replace(" *", ""));
    const example = templateInfo.fields.map(f => f.hint);
    const ws = XLSX.utils.aoa_to_sheet([header, example]);
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, `template_${module}.xlsx`);
  };

  const downloadLog = () => {
    if (!result?.log) return;
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(result.log.map(r => ({
      "Linha": r.row, "Status": r.status, "Nome": r.name, "Motivo": r.reason,
    })));
    XLSX.utils.book_append_sheet(wb, ws, "Log");
    XLSX.writeFile(wb, `import_log_${module}.xlsx`);
  };

  const doPreview = async () => {
    setLoading(true);
    try {
      const payload = { module, rows, mapping, dedup_mode: dedup };
      if (selectedPipeline) payload.pipeline_id = selectedPipeline;
      if (selectedStage)    payload.stage_id    = selectedStage;
      if (Object.keys(stageValueMapping).length)  payload.stage_value_mapping  = stageValueMapping;
      if (Object.keys(statusValueMapping).length) payload.status_value_mapping = statusValueMapping;
      if (Object.keys(responsavelValueMapping).length) payload.responsavel_value_mapping = responsavelValueMapping;
      if (textCase !== "none") payload.text_case = textCase;
      if (tipoNegocio) payload.tipo_negocio = tipoNegocio;
      if (Object.keys(valorMapping).length) payload.valor_mapping = valorMapping;
      const res = await axios.post(`${API}/import/preview`, payload, { withCredentials: true });
      setPreview(res.data);
      if (res.data.mapping) {
        setMapping(prev => {
          const merged = { ...res.data.mapping };
          Object.entries(prev).forEach(([col, field]) => { if (field) merged[col] = field; });
          return merged;
        });
      }
      setStep(4);
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao processar prévia."); }
    setLoading(false);
  };

  const doImport = async () => {
    setLoading(true);
    try {
      saveMapping(module, mapping);
      const payload = { module, rows, mapping, dedup_mode: dedup };
      if (selectedPipeline) payload.pipeline_id = selectedPipeline;
      if (selectedStage)    payload.stage_id    = selectedStage;
      if (Object.keys(stageValueMapping).length)  payload.stage_value_mapping  = stageValueMapping;
      if (Object.keys(statusValueMapping).length) payload.status_value_mapping = statusValueMapping;
      if (Object.keys(responsavelValueMapping).length) payload.responsavel_value_mapping = responsavelValueMapping;
      if (textCase !== "none") payload.text_case = textCase;
      if (tipoNegocio) payload.tipo_negocio = tipoNegocio;
      if (Object.keys(valorMapping).length) payload.valor_mapping = valorMapping;
      const res = await axios.post(`${API}/import/execute`, payload, { withCredentials: true });
      setResult(res.data);
      setStep(5);
      // Refresh history so it appears immediately when user opens the modal
      try {
        const hr = await axios.get(`${API}/import/history`, { withCredentials: true });
        setHistory(hr.data);
      } catch { /* silent */ }
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao importar."); }
    setLoading(false);
  };

  const reset = () => {
    setStep(0); setModule(""); setTemplateInfo(null); setFile(null);
    setRows([]); setHeaders([]); setMapping({}); setPreview(null);
    setResult(null); setDedup("skip");
    setSelectedPipeline(""); setSelectedStage(""); setPipelineStages([]);
    setStageValueMapping({}); setXlsStageValues([]); setXlsStageColName("");
    setStatusValueMapping({}); setXlsStatusValues([]);
    setTextCase("none");
    setTipoNegocio(""); setValorMapping({});
  };

  const fieldOptions = templateInfo?.fields || [];
  const stats = preview?.stats;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${tc500}18` }}>
            <Upload className="w-5 h-5" style={{ color: tc500 }} />
          </div>
          <div>
            <h1 className="text-2xl font-bold font-heading text-slate-900">{t("import.title")}</h1>
            <p className="text-slate-500 text-sm mt-0.5">{t("import.subtitle")}</p>
          </div>
        </div>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={fetchHistory}>
          <History className="w-3.5 h-3.5" />{t("import.history")}
        </Button>
      </div>

      {/* ── HISTORY MODAL ── */}
      {showHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowHistory(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${tc500}18` }}>
                  <History className="w-4.5 h-4.5" style={{ color: tc500 }} />
                </div>
                <div>
                  <h2 className="text-base font-bold text-slate-900">{t("import.history")}</h2>
                  <p className="text-xs text-slate-400">{history.length} importações registradas</p>
                </div>
              </div>
              <button onClick={() => setShowHistory(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            {/* Body */}
            <div className="overflow-y-auto flex-1 px-6 py-4">
              {history.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                  <History className="w-10 h-10 mb-3 opacity-30" />
                  <p className="text-sm">{t("import.historyEmpty")}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {history.map(h => (
                    <div key={h.id} className={`rounded-xl border p-4 transition-all ${h.undone ? "bg-slate-50 border-slate-200 opacity-60" : "bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm"}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          {/* Module + date */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-slate-800">{MODULE_LABELS[h.module] || h.module}</span>
                            <span className="text-xs text-slate-400">·</span>
                            <span className="text-xs text-slate-500">{formatDateTime(h.created_at)}</span>
                            <span className="text-xs text-slate-400">· por {h.user_name}</span>
                            {h.undone && (
                              <span className="text-[10px] font-medium bg-slate-200 text-slate-500 px-2 py-0.5 rounded-full">
                                {t("import.undone")}
                              </span>
                            )}
                          </div>
                          {/* Stats row */}
                          <div className="flex items-center gap-4 mt-2">
                            <div className="flex items-center gap-1.5 text-xs">
                              <div className="w-5 h-5 rounded-md bg-slate-100 flex items-center justify-center">
                                <span className="text-[10px] font-bold text-slate-500">#</span>
                              </div>
                              <span className="text-slate-500">{h.total} linhas</span>
                            </div>
                            <div className="flex items-center gap-3 text-xs">
                              <span className="flex items-center gap-1 text-emerald-600 font-medium">
                                <CheckCircle2 className="w-3 h-3" />{h.results?.imported || 0} importados
                              </span>
                              {(h.results?.updated || 0) > 0 && (
                                <span className="text-amber-500 font-medium">⟳ {h.results.updated} atualizados</span>
                              )}
                              {(h.results?.skipped || 0) > 0 && (
                                <span className="text-slate-400">− {h.results.skipped} ignorados</span>
                              )}
                              {(h.results?.errors || 0) > 0 && (
                                <span className="flex items-center gap-1 text-rose-500 font-medium">
                                  <AlertTriangle className="w-3 h-3" />{h.results.errors} erros
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        {/* Undo button */}
                        {!h.undone && (h.results?.imported || 0) > 0 && (
                          <button onClick={() => undoImport(h)} disabled={undoing === h.id}
                            className="flex items-center gap-1.5 text-xs text-rose-500 hover:text-rose-700 border border-rose-200 rounded-lg px-3 py-1.5 hover:bg-rose-50 transition-colors flex-shrink-0 disabled:opacity-50 font-medium">
                            <Undo2 className="w-3 h-3" />
                            {undoing === h.id ? "Desfazendo..." : t("import.undoImport")}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── UNDO CONFIRMATION MODAL ── */}
      {undoConfirmItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setUndoConfirmItem(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-11 h-11 rounded-xl bg-rose-50 flex items-center justify-center flex-shrink-0">
                <Undo2 className="w-5 h-5 text-rose-500" />
              </div>
              <div>
                <h2 className="text-base font-bold text-slate-900 mb-1">Desfazer importação?</h2>
                <p className="text-sm text-slate-500">
                  Isso irá excluir permanentemente todos os registros criados nesta importação.
                  Registros que foram <strong>atualizados</strong> não serão revertidos.
                </p>
              </div>
            </div>
            {/* Import details */}
            <div className="bg-slate-50 rounded-xl px-4 py-3 mb-5 text-sm space-y-1.5">
              <div className="flex justify-between">
                <span className="text-slate-500">Módulo</span>
                <span className="font-medium text-slate-800">{MODULE_LABELS[undoConfirmItem.module] || undoConfirmItem.module}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Data</span>
                <span className="font-medium text-slate-800">{formatDateTime(undoConfirmItem.created_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Registros a excluir</span>
                <span className="font-bold text-rose-600">{undoConfirmItem.results?.imported || 0}</span>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setUndoConfirmItem(null)}
                className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">
                Cancelar
              </button>
              <button onClick={confirmUndo}
                className="flex-1 py-2.5 rounded-xl bg-rose-500 text-white text-sm font-semibold hover:bg-rose-600 transition-colors flex items-center justify-center gap-2">
                <Undo2 className="w-4 h-4" /> Sim, desfazer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main card */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <StepBar step={step} t={t} tc500={tc500} />

        {/* ── STEP 0: Module selection ── */}
        {step === 0 && (
          <div className="space-y-5">
            <p className="text-sm font-semibold text-slate-700">{t("import.selectModule")}</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {[...Object.entries(MODULE_ICONS), ['unified', Link2]].map(([mod, Icon]) => (
                <button key={mod} type="button"
                  onClick={async () => {
                    setModule(mod);
                    await fetchTemplate(mod);
                    applySavedMapping(mod);
                    if (mod === "negocios" || mod === "unified") {
                      // Show tipo selector before going to upload
                      setTipoNegocio("");
                    } else {
                      setStep(1);
                    }
                  }}
                  className={`flex flex-col items-center gap-2.5 p-5 border rounded-xl hover:-translate-y-0.5 hover:shadow-md transition-all group ${module === mod ? "border-2" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}
                  style={module === mod ? { borderColor: tc500, backgroundColor: `${tc500}08` } : undefined}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform" style={{ backgroundColor: `${tc500}15` }}>
                    <Icon className="w-6 h-6" style={{ color: tc500 }} />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-medium text-slate-700 capitalize">
                      {MODULE_LABELS[mod] || mod}
                    </span>
                    {mod === "unified" && (
                      <Tooltip text={t("import.unifiedDesc") || "Uma linha cria e vincula automaticamente: Negócio + Contato + Empresa + Produto."}>
                        <Info className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0" />
                      </Tooltip>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* Tipo de Negócio selector — appears after selecting Negócios or Unificado */}
            {(module === "negocios" || module === "unified") && (
              <div className="space-y-3 pt-2">
                <div>
                  <p className="text-sm font-semibold text-slate-700">Tipo de Negócio</p>
                  <p className="text-xs text-slate-400 mt-0.5">Define quais campos de valor serão usados na importação.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {/* Recorrente com Setup */}
                  <button type="button"
                    onClick={() => setTipoNegocio("recorrente_setup")}
                    className={`flex flex-col gap-2 px-4 py-4 rounded-xl border-2 text-left transition-all ${tipoNegocio === "recorrente_setup" ? "bg-blue-50" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}
                    style={tipoNegocio === "recorrente_setup" ? { borderColor: tc500 } : undefined}>
                    <span className="text-sm font-semibold text-slate-800">Rec. com Setup</span>
                    <div className="flex flex-wrap gap-1.5">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">MRR</span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">Setup</span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-medium">Valor (auto)</span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-medium">TCV (auto)</span>
                    </div>
                  </button>
                  {/* Venda Pontual */}
                  <button type="button"
                    onClick={() => setTipoNegocio("pontual")}
                    className={`flex flex-col gap-2 px-4 py-4 rounded-xl border-2 text-left transition-all ${tipoNegocio === "pontual" ? "bg-blue-50" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}
                    style={tipoNegocio === "pontual" ? { borderColor: tc500 } : undefined}>
                    <span className="text-sm font-semibold text-slate-800">Venda Pontual</span>
                    <div className="flex flex-wrap gap-1.5">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">Valor do Negócio</span>
                    </div>
                  </button>
                  {/* Recorrente por Produto */}
                  <div className="flex flex-col gap-2 px-4 py-4 rounded-xl border-2 border-slate-200 bg-slate-50 opacity-70">
                    <span className="text-sm font-semibold text-slate-500">Rec. por Produto</span>
                    <p className="text-[11px] text-slate-400 leading-relaxed">
                      Importação unificada com produtos não é suportada no momento. Importe os negócios separadamente e vincule os produtos manualmente.
                    </p>
                  </div>
                </div>
                {tipoNegocio && (
                  <div className="flex justify-end pt-2">
                    <Button onClick={() => setStep(1)} className="gap-2 text-white" style={{ backgroundColor: tc500 }}>
                      Continuar <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── STEP 1: Upload ── */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-700">
                <span className="font-semibold">{MODULE_LABELS[module] || module}</span>
                <span className="text-slate-400 font-normal"> — Selecione um arquivo para importar</span>
              </p>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={downloadTemplate}>
                <Download className="w-3.5 h-3.5" />{t("import.downloadTemplate")}
              </Button>
            </div>
            {/* Template preview */}
            {templateInfo && (
              <div className="bg-slate-50 rounded-lg px-4 py-3 text-xs text-slate-500 space-y-1">
                <p className="font-semibold text-slate-600 mb-2">Colunas esperadas:</p>
                <div className="flex flex-wrap gap-2">
                  {templateInfo.fields.map(f => (
                    <span key={f.key} className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${f.required ? "bg-rose-100 text-rose-700" : "bg-slate-200 text-slate-600"}`}>
                      {f.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {/* Drop zone */}
            <div ref={dropRef}
              onDrop={handleDrop}
              onDragOver={e => e.preventDefault()}
              onClick={() => document.getElementById("file-input").click()}
              className="border-2 border-dashed border-slate-200 rounded-xl p-10 flex flex-col items-center gap-3 cursor-pointer hover:border-slate-300 hover:bg-slate-50 transition-all">
              <FileSpreadsheet className="w-10 h-10 text-slate-300" />
              <p className="text-sm font-medium text-slate-600">{t("import.uploadTitle")}</p>
              <p className="text-xs text-slate-400">{t("import.uploadHint")}</p>
              <Button variant="outline" size="sm" style={{ borderColor: tc500, color: tc500 }}>
                {t("common.select") || "Selecionar arquivo"}
              </Button>
            </div>
            <input id="file-input" type="file" accept=".xlsx,.xls,.csv" className="hidden"
              onChange={e => e.target.files[0] && parseFile(e.target.files[0])} />
            {/* Nav footer */}
            <div className="flex items-center justify-between pt-4 border-t border-slate-100 mt-2">
              <Button variant="outline" className="gap-1.5" onClick={() => { setStep(0); setModule(""); setTemplateInfo(null); }}>
                <ArrowLeft className="w-3.5 h-3.5" /> Voltar
              </Button>
              <p className="text-xs text-slate-400">Selecione ou arraste um arquivo para avançar</p>
            </div>
          </div>
        )}

        {/* ── STEP 2: Mapping ── */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-700">{t("import.mapping")}</p>
                <p className="text-xs text-slate-400 mt-0.5">{t("import.mappingDesc")}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400">{file?.name}</span>
                {savedMapping && (
                  <button type="button"
                    onClick={() => setMapping(prev => {
                      const merged = { ...prev };
                      Object.entries(savedMapping).forEach(([col, field]) => { if (!merged[col]) merged[col] = field; });
                      return merged;
                    })}
                    className="text-xs flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors">
                    <RotateCcw className="w-3 h-3" />{t("import.lastMapping") || "Último mapeamento"}
                  </button>
                )}
              </div>
            </div>
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="grid grid-cols-2 gap-0 bg-slate-50 px-4 py-2.5 border-b border-slate-200 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                <span>{t("import.columnInFile")}</span>
                <span>{t("import.fieldInSystem")}</span>
              </div>
              <div className="divide-y divide-slate-100">
                {headers.map(h => (
                  <div key={h} className={`grid grid-cols-2 gap-4 px-4 py-2.5 items-center transition-colors ${mapping[h] ? "bg-emerald-50/60" : ""}`}>
                    <span className={`text-sm font-mono truncate ${mapping[h] ? "text-emerald-800" : "text-slate-700"}`}>{h}</span>
                    <Select value={mapping[h] || "__ignore__"} onValueChange={v => setMapping(prev => {
                      const n = { ...prev };
                      if (v === "__ignore__") delete n[h]; else n[h] = v;
                      return n;
                    })}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__ignore__" className="text-xs text-slate-400">{t("import.ignore")}</SelectItem>
                        {(() => {
                          // Group fields by entity (group property) or all together for single modules
                          const hasGroups = fieldOptions.some(f => f.group);
                          if (!hasGroups) {
                            return fieldOptions.map(f => (
                              <SelectItem key={f.key} value={f.key} className="text-xs">
                                {f.label}{f.required ? ` (${t("import.required")})` : ""}
                                {f.custom ? " ★" : ""}
                              </SelectItem>
                            ));
                          }
                          // Grouped by entity
                          const GROUP_LABELS = {
                            negocio: "Negócio", contato: "Contato",
                            empresa: "Empresa", produto: "Produto",
                          };
                          const groups = {};
                          const ungrouped = [];
                          fieldOptions.forEach(f => {
                            if (f.group) {
                              if (!groups[f.group]) groups[f.group] = [];
                              groups[f.group].push(f);
                            } else {
                              ungrouped.push(f);
                            }
                          });
                          return [
                            ...Object.entries(groups).map(([grp, gFields]) => (
                              <SelectGroup key={grp}>
                                <SelectLabel className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                  {GROUP_LABELS[grp] || grp}
                                </SelectLabel>
                                {gFields.map(f => (
                                  <SelectItem key={f.key} value={f.key} className="text-xs pl-6">
                                    {f.label}{f.required ? ` (${t("import.required")})` : ""}
                                    {f.custom ? " ★" : ""}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            )),
                            ...ungrouped.map(f => (
                              <SelectItem key={f.key} value={f.key} className="text-xs">
                                {f.label}{f.required ? ` (${t("import.required")})` : ""}
                                {f.custom ? " ★" : ""}
                              </SelectItem>
                            )),
                          ];
                        })()}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between pt-4 border-t border-slate-100">
              <Button variant="outline" className="gap-1.5" onClick={() => setStep(1)}>
                <ArrowLeft className="w-3.5 h-3.5" /> Voltar
              </Button>
              <Button onClick={() => { fetchPipelines(); setStep(3); }} className="gap-2 text-white" style={{ backgroundColor: tc500 }}>
                {t("common.continue")} <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 3: Configurações ── */}
        {step === 3 && (
          <div className="space-y-6">
            <p className="text-sm font-semibold text-slate-700">Configurações de importação</p>

            {/* Pipeline selector */}
            {(module === "negocios" || module === "unified") && (
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Pipeline de destino</Label>
                  <p className="text-xs text-slate-400 mb-3">Os negócios serão importados para este pipeline.</p>
                  {pipelines.length === 0 ? (
                    <p className="text-xs text-slate-400 italic">Carregando pipelines...</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {pipelines.map(p => (
                        <button key={p.id} type="button"
                          onClick={() => { setSelectedPipeline(p.id); fetchStages(p.id); }}
                          className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left transition-all ${selectedPipeline === p.id ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}>
                          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: selectedPipeline === p.id ? tc500 : "#cbd5e1" }} />
                          <span className="text-sm font-medium text-slate-800 truncate flex-1">{p.name}</span>
                          {selectedPipeline === p.id && <CheckCircle2 className="w-4 h-4 text-blue-500 flex-shrink-0" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Stage VALUE mapping: XLS stage name → pipeline stage */}
                {xlsStageValues.length > 0 && pipelineStages.length > 0 && (
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">
                      Mapear etapas do arquivo
                    </Label>
                    <p className="text-xs text-slate-400 mb-3">
                      {xlsStageValues.length} valores únicos encontrados na coluna <span className="font-semibold text-slate-500">"{xlsStageColName}"</span> do arquivo. Associe cada um a uma etapa da pipeline.
                    </p>
                    <div className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className="grid grid-cols-[1fr,auto,1fr] gap-0 bg-slate-50 px-4 py-2.5 border-b border-slate-200 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                        <span>Valor no arquivo</span>
                        <span></span>
                        <span>Etapa na pipeline</span>
                      </div>
                      <div className="divide-y divide-slate-100">
                        {xlsStageValues.map(val => (
                          <div key={val} className="grid grid-cols-[1fr,auto,1fr] gap-4 px-4 py-2.5 items-center">
                            <div className="flex items-center gap-2">
                              <span className="inline-flex items-center gap-1.5 text-sm font-mono bg-amber-50 text-amber-800 border border-amber-200 rounded px-2 py-0.5">
                                <FileSpreadsheet className="w-3 h-3 text-amber-500 flex-shrink-0" />
                                {val}
                              </span>
                            </div>
                            <ArrowRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                            <Select
                              value={stageValueMapping[val] || "__none__"}
                              onValueChange={sid => setStageValueMapping(prev => {
                                const n = { ...prev };
                                if (sid === "__none__") delete n[val]; else n[val] = sid;
                                return n;
                              })}>
                              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Não mapeado" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__" className="text-xs text-slate-400">— Não mapear</SelectItem>
                                {pipelineStages.map(s => (
                                  <SelectItem key={s.id} value={s.id} className="text-xs">
                                    <div className="flex items-center gap-2">
                                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: s.color || tc500 }} />
                                      {s.name}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-2">
                      {Object.keys(stageValueMapping).length} de {xlsStageValues.length} etapas mapeadas
                      {Object.keys(stageValueMapping).length > 0 && ` · ${xlsStageValues.filter(v => !stageValueMapping[v]).length} sem mapeamento usarão a etapa inicial`}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Responsavel mapping — always show for negocios/unified */}
            {(module === "negocios" || module === "unified") && (
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">
                  Mapear responsáveis
                </Label>
                {xlsResponsavelValues.length > 0 ? (
                  <>
                    <p className="text-xs text-slate-400 mb-3">
                      {xlsResponsavelValues.length} valores únicos encontrados na coluna do arquivo. Associe cada um a um responsável do sistema.
                    </p>
                    <div className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className="grid grid-cols-[1fr,auto,1fr] gap-0 bg-slate-50 px-4 py-2.5 border-b border-slate-200 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                        <span>Valor no arquivo</span>
                        <span></span>
                        <span>Responsável no sistema</span>
                      </div>
                      <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
                        {xlsResponsavelValues.map(val => (
                          <div key={val} className="grid grid-cols-[1fr,auto,1fr] gap-4 px-4 py-2.5 items-center">
                            <span className="inline-flex items-center text-sm font-mono bg-violet-50 text-violet-800 border border-violet-200 rounded px-2 py-0.5 truncate">{val}</span>
                            <ArrowRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                            <Select
                              value={responsavelValueMapping[val] || "__none__"}
                              onValueChange={uid => setResponsavelValueMapping(prev => {
                                const n = { ...prev };
                                if (uid === "__none__") delete n[val]; else n[val] = uid;
                                return n;
                              })}>
                              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Não mapeado" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__" className="text-xs text-slate-400">— Não mapear</SelectItem>
                                {systemUsers.map(u => (
                                  <SelectItem key={u.user_id} value={u.user_id} className="text-xs">{u.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-2">
                      {Object.keys(responsavelValueMapping).length} de {xlsResponsavelValues.length} responsáveis mapeados
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-slate-400 mb-3">
                      Nenhuma coluna mapeada para "Responsável" no arquivo. Selecione um responsável padrão para todos os negócios importados.
                    </p>
                    <Select
                      value={responsavelValueMapping["__default__"] || "__none__"}
                      onValueChange={uid => setResponsavelValueMapping(uid === "__none__" ? {} : { "__default__": uid })}>
                      <SelectTrigger className="h-9 w-full"><SelectValue placeholder="Selecionar responsável padrão..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__" className="text-xs text-slate-400">— Usar o usuário que importa</SelectItem>
                        {systemUsers.map(u => (
                          <SelectItem key={u.user_id} value={u.user_id} className="text-xs">{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </>
                )}
              </div>
            )}

            {/* ── Valor field mapping by Tipo de Negócio ── */}
            {tipoNegocio && (module === "negocios" || module === "unified") && (
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">
                  Mapeamento de valores — {tipoNegocio === "recorrente_setup" ? "Rec. com Setup" : "Venda Pontual"}
                </Label>
                <p className="text-xs text-slate-400 mb-3">
                  {tipoNegocio === "recorrente_setup"
                    ? "Associe as colunas do arquivo que contêm o Setup e MRR. Valor do Negócio e TCV serão calculados automaticamente."
                    : "Associe a coluna do arquivo que contém o Valor do Negócio."}
                </p>
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <div className="grid grid-cols-2 gap-0 bg-slate-50 px-4 py-2.5 border-b border-slate-200 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    <span>Campo do sistema</span>
                    <span>Coluna no arquivo</span>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {tipoNegocio === "recorrente_setup" ? (
                      <>
                        {[
                          { key: "setup", label: "Setup" },
                          { key: "mrr", label: "MRR" },
                        ].map(f => (
                          <div key={f.key} className="grid grid-cols-2 gap-4 px-4 py-2.5 items-center">
                            <div className="flex items-center gap-2">
                              <DollarSign className="w-3.5 h-3.5 text-slate-400" />
                              <span className="text-sm font-medium text-slate-700">{f.label}</span>
                            </div>
                            <Select
                              value={valorMapping[f.key] || "__none__"}
                              onValueChange={v => setValorMapping(prev => {
                                const n = { ...prev };
                                if (v === "__none__") delete n[f.key]; else n[f.key] = v;
                                return n;
                              })}>
                              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Selecionar coluna..." /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__" className="text-xs text-slate-400">— Não mapear</SelectItem>
                                {headers.map(h => (
                                  <SelectItem key={h} value={h} className="text-xs font-mono">{h}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                        {/* Calculated fields preview */}
                        <div className="grid grid-cols-2 gap-4 px-4 py-2.5 items-center bg-emerald-50/50">
                          <div className="flex items-center gap-2">
                            <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />
                            <span className="text-sm font-medium text-emerald-700">Valor do Negócio</span>
                          </div>
                          <span className="text-xs text-emerald-600 font-medium italic">Calculado: MRR + Setup</span>
                        </div>
                        <div className="grid grid-cols-2 gap-4 px-4 py-2.5 items-center bg-emerald-50/50">
                          <div className="flex items-center gap-2">
                            <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />
                            <span className="text-sm font-medium text-emerald-700">TCV</span>
                          </div>
                          <span className="text-xs text-emerald-600 font-medium italic">Calculado: (MRR x 12) + Setup</span>
                        </div>
                      </>
                    ) : (
                      <div className="grid grid-cols-2 gap-4 px-4 py-2.5 items-center">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-3.5 h-3.5 text-slate-400" />
                          <span className="text-sm font-medium text-slate-700">Valor do Negócio</span>
                        </div>
                        <Select
                          value={valorMapping["valor_negocio"] || "__none__"}
                          onValueChange={v => setValorMapping(prev => {
                            const n = { ...prev };
                            if (v === "__none__") delete n["valor_negocio"]; else n["valor_negocio"] = v;
                            return n;
                          })}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Selecionar coluna..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__" className="text-xs text-slate-400">— Não mapear</SelectItem>
                            {headers.map(h => (
                              <SelectItem key={h} value={h} className="text-xs font-mono">{h}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Status VALUE mapping */}
            {xlsStatusValues.length > 0 && (module === "negocios" || module === "unified") && (
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">
                  Mapear status do arquivo
                </Label>
                <p className="text-xs text-slate-400 mb-3">
                  Associe cada status do arquivo ao status do sistema. Para Ganho ou Perdido, escolha o campo com a data de conclusão.
                </p>
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <div className="grid grid-cols-3 gap-0 bg-slate-50 px-4 py-2.5 border-b border-slate-200 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    <span>Status no arquivo</span>
                    <span>Status no sistema</span>
                    <span>Campo de data</span>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {xlsStatusValues.map(val => {
                      const current = statusValueMapping[val] || { status: null, dateCol: "" };
                      const needsDate = current.status === "ganho" || current.status === "perdido";
                      return (
                        <div key={val} className="grid grid-cols-3 gap-3 px-4 py-2.5 items-center">
                          <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0" />
                            <span className="text-sm font-mono text-slate-700 truncate">{val}</span>
                          </div>
                          <Select
                            value={current.status ?? "__none__"}
                            onValueChange={s => setStatusValueMapping(prev => ({
                              ...prev, [val]: { ...current, status: s === "__none__" ? null : s }
                            }))}>
                            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__" className="text-xs text-slate-400">— Em aberto</SelectItem>
                              <SelectItem value="ganho"   className="text-xs text-emerald-600 font-medium">✓ Ganho</SelectItem>
                              <SelectItem value="perdido" className="text-xs text-rose-500 font-medium">✗ Perdido</SelectItem>
                            </SelectContent>
                          </Select>
                          {needsDate ? (
                            <Select
                              value={current.dateCol || "__none__"}
                              onValueChange={col => setStatusValueMapping(prev => ({
                                ...prev, [val]: { ...current, dateCol: col === "__none__" ? "" : col }
                              }))}>
                              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Coluna de data..." /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__" className="text-xs text-slate-400">— Não mapear</SelectItem>
                                {headers.map(h => (
                                  <SelectItem key={h} value={h} className="text-xs font-mono">{h}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="text-xs text-slate-300 italic">—</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Text case transformation */}
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Formatação de textos</Label>
              <p className="text-xs text-slate-400 mb-3">Aplica transformação em todos os campos de texto (nome, descrição, etc.) ao importar.</p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: "none",       label: "Como está",   example: "João da Silva" },
                  { value: "capitalize", label: "Capitalize",  example: "João Da Silva" },
                  { value: "upper",      label: "CAIXA ALTA",  example: "JOÃO DA SILVA" },
                ].map(opt => (
                  <button key={opt.value} type="button"
                    onClick={() => setTextCase(opt.value)}
                    className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border-2 transition-all ${textCase === opt.value ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}>
                    <span className={`text-sm font-semibold ${textCase === opt.value ? "text-blue-700" : "text-slate-700"}`}>{opt.label}</span>
                    <span className="text-[11px] text-slate-400 font-mono">{opt.example}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Dedup */}
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">{t("import.dedup")}</Label>
              <p className="text-xs text-slate-400 mb-3">O que fazer se um registro com o mesmo nome já existir.</p>
              <Select value={dedup} onValueChange={setDedup}>
                <SelectTrigger className="max-w-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="skip">{t("import.dedupSkip")}</SelectItem>
                  <SelectItem value="update">{t("import.dedupUpdate")}</SelectItem>
                  <SelectItem value="always">{t("import.dedupAlways")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-slate-100">
              <Button variant="outline" className="gap-1.5" onClick={() => setStep(2)}>
                <ArrowLeft className="w-3.5 h-3.5" /> Voltar
              </Button>
              <Button onClick={doPreview} disabled={loading} className="gap-2 text-white" style={{ backgroundColor: tc500 }}>
                {loading ? t("common.loading") : t("common.continue")} <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 4: Preview ── */}
        {step === 4 && preview && (
          <div className="space-y-4">
            <p className="text-sm font-semibold text-slate-700">{t("import.previewTitle")}</p>
            <p className="text-xs text-slate-400">{t("import.previewDesc")}</p>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: t("import.valid"),    value: stats.valid,      color: "#10b981", bg: "#ecfdf5" },
                { label: t("import.warning"),  value: stats.warnings,   color: "#f59e0b", bg: "#fffbeb" },
                { label: t("import.error"),    value: stats.errors,     color: "#ef4444", bg: "#fef2f2" },
                { label: t("import.duplicate"),value: stats.duplicates, color: "#94a3b8", bg: "#f8fafc" },
              ].map(s => (
                <div key={s.label} className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ backgroundColor: s.bg }}>
                  <span className="text-xs font-medium" style={{ color: s.color }}>{s.label}</span>
                  <span className="text-lg font-bold" style={{ color: s.color }}>{s.value}</span>
                </div>
              ))}
            </div>

            {/* Warnings */}
            {preview.requiredMissing?.length > 0 && (
              <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2.5 text-xs text-rose-700">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span><strong>{t("import.requiredMissing")}:</strong> {preview.requiredMissing.join(", ")}</span>
              </div>
            )}
            {preview.unmapped_columns?.length > 0 && (
              <p className="text-xs text-slate-400">
                <strong>{preview.unmapped_columns.length}</strong> {t("import.unmappedWarning")}: {preview.unmapped_columns.join(", ")}
              </p>
            )}

            {/* Preview table */}
            <div className="border border-slate-200 rounded-xl overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-slate-500 uppercase tracking-wider">#</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                    {Object.keys(preview.preview[0]?.mapped || {}).map(k => (
                      <th key={k} className="px-3 py-2 text-left font-semibold text-slate-500 uppercase tracking-wider">{k}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {preview.preview.slice(0, 20).map((row, i) => (
                    <tr key={i} className={row.status === "error" ? "bg-rose-50" : row.status === "warning" ? "bg-amber-50/50" : ""}>
                      <td className="px-3 py-2 text-slate-400">{row.row_index + 2}</td>
                      <td className="px-3 py-2">
                        {row.errors.length > 0 ? (
                          <span className="text-rose-600 font-medium flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />{row.errors[0]}
                          </span>
                        ) : row.is_duplicate ? (
                          <span className="text-amber-600 font-medium">{t("import.duplicate")}</span>
                        ) : (
                          <span className="text-emerald-600 font-medium flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" />{t("import.valid")}
                          </span>
                        )}
                      </td>
                      {Object.values(row.mapped).map((v, j) => (
                        <td key={j} className="px-3 py-2 text-slate-600 max-w-[120px] truncate">{String(v)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.preview.length > 20 && (
                <p className="px-3 py-2 text-xs text-slate-400 border-t border-slate-100">
                  Mostrando 20 de {preview.stats.total} linhas...
                </p>
              )}
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-slate-100">
              <Button variant="outline" className="gap-1.5" onClick={() => setStep(3)}>
                <ArrowLeft className="w-3.5 h-3.5" /> Voltar
              </Button>
              <Button onClick={doImport} disabled={loading || preview.requiredMissing?.length > 0}
                className="gap-2 text-white" style={{ backgroundColor: tc500 }}>
                {loading ? t("import.importing") : t("import.execute")}
                <Upload className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 5: Result ── */}
        {step === 5 && result && (
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-500" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">{t("import.resultTitle")}</h3>
                <p className="text-sm text-slate-500">{MODULE_LABELS[module] || module} · {Object.values(result.results).reduce((a,b)=>a+b,0)} linhas processadas</p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { key: "imported", label: t("import.imported"), color: "#10b981", bg: "#ecfdf5" },
                { key: "updated",  label: t("import.updated"),  color: "#3b82f6", bg: "#eff6ff" },
                { key: "skipped",  label: t("import.skipped"),  color: "#94a3b8", bg: "#f8fafc" },
                { key: "errors",   label: t("import.errors"),   color: "#ef4444", bg: "#fef2f2" },
              ].map(s => (
                <div key={s.key} className="px-4 py-3 rounded-xl text-center" style={{ backgroundColor: s.bg }}>
                  <p className="text-2xl font-bold" style={{ color: s.color }}>{result.results[s.key] || 0}</p>
                  <p className="text-xs font-medium mt-0.5" style={{ color: s.color }}>{s.label}</p>
                </div>
              ))}
            </div>

            {/* Error log */}
            {result.results.errors > 0 && (
              <div className="border border-rose-200 rounded-xl overflow-hidden">
                <div className="bg-rose-50 px-4 py-2.5 flex items-center justify-between">
                  <span className="text-xs font-semibold text-rose-700">{result.results.errors} {t("import.errors")}</span>
                  <button onClick={downloadLog} className="text-xs text-rose-600 flex items-center gap-1 hover:underline">
                    <Download className="w-3 h-3" />{t("import.downloadLog")}
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto divide-y divide-rose-100">
                  {result.log.filter(r => r.status === "error").slice(0, 20).map((r, i) => (
                    <div key={i} className="px-4 py-2 text-xs flex items-center gap-3">
                      <span className="text-slate-400 w-10 flex-shrink-0">Linha {r.row}</span>
                      <span className="text-slate-600 flex-1 truncate font-medium">{r.name}</span>
                      <span className="text-rose-600">{r.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.results.errors === 0 && (
              <p className="text-sm text-emerald-600 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />{t("import.noErrors")}
              </p>
            )}

            <Button onClick={reset} variant="outline" className="gap-1.5">
              <RotateCcw className="w-3.5 h-3.5" />{t("import.newImport")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
